import * as React from 'react';
import type {
  InvoiceGroupBy,
  InvoiceResponseDTO,
  InvoiceSortBy,
} from '@relay-towing/api';
import {
  ListColumnRow,
  ListToolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  TextLink,
  toneForStatus,
  type ListMode,
  sentenceCase,
} from '@relay-towing/ui';
import { Link, useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useMember } from '../../hooks/useMember';
import { usePagedList } from '../../hooks/usePagedList';
import { PagedRows } from '../shell/PagedRows';
import { FunctionPage } from '../functions/FunctionPage';
import { SortFilters } from '../shell/SortFilters';
import { NewInvoiceButton } from './NewInvoiceButton';
import { vehicleOf } from '../vehicle';

type View = 'current' | 'archived';
type Sort = Extract<InvoiceSortBy, 'newest' | 'oldest'>;

const SORTS: Array<{ value: Sort; label: string }> = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

/** The list row's invoice: the DTO, plus the call it is on when there is one. */
type InvoiceRow = InvoiceResponseDTO & {
  dispatch?: { dispatchNumber?: string | null } | null;
};

const money = (amount: number) =>
  amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const issued = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' })
    : undefined;

const vehicleOfInvoice = (invoice: InvoiceRow) =>
  vehicleOf({
    vehicleYear: invoice.vehicleYear,
    vehicleMake: invoice.vehicleMake,
    vehicleModel: invoice.vehicleModel,
  });

/**
 * The invoices tab: Current, grouped by status on the server (Draft, Sent,
 * Processing, Paid), and Archived apart. One list for everyone; whose
 * invoices are in it is the server's answer, the company's with "View all
 * invoices" and those on the member's own calls with "View own". The app
 * asked for a driver's with `assignedToMe` and drew them as one ungrouped
 * list with no Archived, a second shape for what the server already scopes.
 * The tab shows to anyone who may see invoices, not only to those who may
 * make them, which hid it from drivers who could view their own.
 *
 * Drawn as the grouped list the impounds and inspections are (Figma
 * 45-5523): a band per status, a row of number, customer and total, and the
 * same invoices as a table.
 */
export function InvoicesScreen() {
  const navigate = useNavigate();
  const member = useMember();
  const [view, setView] = React.useState<View>('current');
  const [sort, setSort] = React.useState<Sort>('newest');
  const [mode, setMode] = React.useState<ListMode>('list');
  const [search, setSearch] = React.useState('');
  const searchQuery = useDebouncedValue(search).trim() || undefined;

  const params =
    view === 'archived'
      ? { status: 'ARCHIVED', sortBy: sort, searchQuery }
      : {
          groupBy: 'status' satisfies InvoiceGroupBy,
          sortBy: sort,
          searchQuery,
        };
  const list = usePagedList<InvoiceRow>('mobile-invoices', '/invoices', params);
  const open = (invoice: InvoiceRow) => navigate(`/invoices/${invoice.id}`);

  return (
    <FunctionPage
      id="invoices"
      title="Invoices"
      actions={
        // The office invoices from any call it may; someone who only drives
        // does it from their own call, which is where they are.
        member.officeRole && member.can('invoices.create') ? (
          <NewInvoiceButton />
        ) : undefined
      }
    >
      <Tabs value={view} onValueChange={(next) => setView(next as View)}>
        <TabsList fullWidth>
          <TabsTrigger value="current" fullWidth>
            Current
          </TabsTrigger>
          <TabsTrigger value="archived" fullWidth>
            Archived
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <PagedRows
        list={list}
        empty={searchQuery ? 'No invoices match' : 'No invoices yet'}
        groups={
          view === 'current'
            ? {
                of: (invoice) => invoice.status,
                label: sentenceCase,
                tone: toneForStatus,
              }
            : undefined
        }
        toolbar={
          <ListToolbar
            mode={mode}
            onModeChange={setMode}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Customer or number"
            searchLabel="Search invoices"
          >
            <SortFilters options={SORTS} value={sort} onValueChange={setSort} />
          </ListToolbar>
        }
        table={
          mode === 'table'
            ? (invoices) => (
                <Table shape="band">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Call</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead numeric>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow
                        key={invoice.id}
                        interactive
                        onClick={() => open(invoice)}
                      >
                        <TableCell>
                          {/* The row tap is the convenience; the link is
                              the way in from a keyboard. */}
                          <TextLink asChild>
                            <Link
                              to={`/invoices/${invoice.id}`}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {invoice.invoiceNumber}
                            </Link>
                          </TextLink>
                        </TableCell>
                        <TableCell>{invoice.clientName || '—'}</TableCell>
                        <TableCell>
                          {vehicleOfInvoice(invoice) || '—'}
                        </TableCell>
                        <TableCell>
                          {invoice.dispatch?.dispatchNumber
                            ? `#${invoice.dispatch.dispatchNumber}`
                            : '—'}
                        </TableCell>
                        <TableCell>{issued(invoice.issuedOn) ?? '—'}</TableCell>
                        {/* Words, as the design's table has them. */}
                        <TableCell>{sentenceCase(invoice.status)}</TableCell>
                        <TableCell numeric>{money(invoice.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            : undefined
        }
      >
        {(invoice) => (
          <ListColumnRow
            key={invoice.id}
            lead={invoice.invoiceNumber}
            trail={money(invoice.total)}
            onClick={() => open(invoice)}
          >
            {invoice.clientName || vehicleOfInvoice(invoice) || '—'}
          </ListColumnRow>
        )}
      </PagedRows>
    </FunctionPage>
  );
}
